'use client'

import { Divider } from '@heroui/react'
import { useTranslations } from 'next-intl'
import { ChangeEventHandler, use, useMemo, useState } from 'react'
import { SearchDrawer } from './drawer'
import { ProductFilters } from '@/features/product/model/product-filters'
import { Category } from '@/features/categories/model/category.model'
import { useUpdateSearchParams } from '@/core/hooks/use-update-search-params'
import { CategoryFilter } from './category-filter'
import { AttributeFilter } from './attributes-filter'
import { PriceFilter } from './price-filter'
import { FilteringAttributes } from '@/features/product/model/filtering-attributes'
import { debounce } from '@/core/lib/utils/debounce'
import { Button } from '@/core/ui/atoms/button'
import { getChildCategories } from '@/features/categories/providers/server'

type Props = {
  filters: Partial<ProductFilters>
  categoriesPromise: Promise<Category[]>
  attributesPromise: Promise<FilteringAttributes[]>
}

export function SearchSidebar({
  filters,
  attributesPromise,
  categoriesPromise,
}: Props) {
  const t = useTranslations()
  const { updateSearchParam } = useUpdateSearchParams()
  const categories = use(categoriesPromise) || []
  const attributes = use(attributesPromise) || []

  const [search, setSearch] = useState(filters.search ?? '')
  const handleSearchChange: ChangeEventHandler<HTMLInputElement> = e => {
    setSearch(e.target.value)

    debounce((search: string) => updateSearchParam({ search }))(e.target.value)
  }

  const handleResetFilter = () => {
    updateSearchParam({
      categories: [],
      attribute: [],
      min_price: [],
      max_price: [],
      sorting: [],
    })
  }

  const activeFilterCount = useMemo(
    () =>
      Object.values(filters)
        .filter(Boolean)
        .reduce((count, val) => {
          if (Array.isArray(val)) return Number(count) + Number(val.length)
          return Number(count) + 1
        }, 0 as number),
    [filters],
  ) as number

  const content = (
    <div className='text-mountain-bluebird space-y-4'>
      <input
        type='text'
        placeholder='Red shoes'
        value={search}
        className='w-full rounded-md border px-3 py-2 text-sm'
        onChange={handleSearchChange}
      />

      <Divider />

      <CategoryFilter
        categories={categories}
        selected={filters.categories}
        fetchChildren={getChildCategories}
        onSelect={(ids: number | number[]) => {
          const values = Array.isArray(ids) ? ids : [ids]
          const current = filters.categories || []

          let updated: number[]

          if (Array.isArray(ids)) {
            updated = current.filter(id => !values.includes(id))
          } else {
            updated = current.includes(ids)
              ? current.filter(id => id !== ids)
              : [...current, ids]
          }

          updateSearchParam({ categories: updated.join(',') })
        }}
      />

      <Divider />
      {attributes.map(attr => (
        <AttributeFilter
          key={attr.id}
          label={attr.name}
          values={attr.value}
          selectedValues={filters.attribute
            ?.filter(a => a.startsWith(`${attr.id}.[`))
            .map(a => {
              const match = a.match(/\[\s*(.*?)\s*\]/)
              return match ? match[1].split(',').map(Number) : []
            })
            .flat()}
          onSelect={value => {
            const current =
              filters.attribute?.filter(a => !a.startsWith(`${attr.id}.[`)) ||
              []

            const existing =
              filters.attribute
                ?.filter(a => a.startsWith(`${attr.id}.[`))
                .map(a => {
                  const match = a.match(/\[\s*(.*?)\s*\]/)
                  return match ? match[1].split(',').map(Number) : []
                })
                .flat() || []

            const valueNum = Number(value)
            const newValues = existing.includes(valueNum)
              ? existing.filter(v => v !== valueNum)
              : [...existing, valueNum]

            if (newValues.length > 0) {
              current.push(`${attr.id}.[${newValues.join(',')}]`)
            }

            updateSearchParam({ attribute: current })
          }}
        />
      ))}

      <Divider />

      <PriceFilter
        min={filters.min_price}
        max={filters.max_price}
        onChange={({ min, max }) =>
          updateSearchParam({ min_price: min, max_price: max })
        }
      />
    </div>
  )

  return (
    <div>
      <div className='mb-3 flex justify-between'>
        <h3 className='hidden text-lg text-gray-600 md:block'>
          {t('search.Filters')}{' '}
          <span className='bg-creamed-cron rounded px-1 text-sm'>
            {activeFilterCount}
          </span>
        </h3>
        <Button onClick={() => handleResetFilter()}>{t('search.reset')}</Button>
      </div>

      <nav className='hidden w-96 rounded-xl bg-white p-3 shadow-sm md:block'>
        {content}
      </nav>

      <div className='flex items-center gap-2 md:hidden'>
        <SearchDrawer>{content}</SearchDrawer>
        <h3 className='text-lg text-gray-600'>
          {t('search.Filters')}{' '}
          <span className='bg-creamed-cron rounded px-1 text-sm'>
            {activeFilterCount}
          </span>
        </h3>
      </div>
    </div>
  )
}
